/**
 * The device builds of one service, newest first: status, platform · kind · who builds it, who started it, how long it
 * took, the message; View log, Cancel (queued / running), Delete, a download per file, "Install on a phone" (a QR
 * code) for an APK, and "Open in Expo" / "Open in Codemagic" for a build made there. The list itself comes from
 * useDeviceBuilds (read every 5 s while a build is queued or running); `onChanged` reads it again after an action.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Popconfirm, Tag, message as toast } from 'antd';
import { cancelDeviceBuild, deleteDeviceBuild, downloadArtifact, isActiveBuild } from '../../services/DeviceBuildsService';
import DeviceBuildLog from './DeviceBuildLog';
import InstallQr from './InstallQr';
import {
  ARTIFACT_KIND, PLATFORM_LABEL, PROVIDER_SHORT, PROVIDER_SITE, STATUS, VARIANT_LABEL, buildDuration, formatSize,
} from './deviceBuildsText';
import './DeviceBuilds.css';

const when = (iso) => {
  const time = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(time) ? new Date(time).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : null;
};

export default function DeviceBuildsList({ projectId, builds = [], readOnly = false, onChanged = null }) {
  const [log, setLog] = useState(null);
  const [qr, setQr] = useState(null);
  const [busy, setBusy] = useState(null);

  const act = async(key, work, failure) => {
    setBusy(key);
    try {
      await work();
      onChanged?.();
    } catch (err) {
      toast.error(err?.message || failure);
    } finally {
      setBusy(null);
    }
  };

  if (builds.length === 0) {
    return <p className="devb-muted" data-testid="devb-list-empty">No builds yet.</p>;
  }

  return (
    <>
      <ul className="devb-list" data-testid="devb-list">
        {builds.map((build) => {
          const status = STATUS[build.status] || { label: build.status, color: 'default' };
          const duration = buildDuration(build);
          const started = when(build.startedAt || build.createdAt);
          const site = PROVIDER_SITE[build.provider];
          return (
            <li className="devb-item" data-testid={`devb-build-${build.id}`} key={build.id}>
              <div className="devb-item-head">
                <Tag color={status.color} data-testid="devb-status">{status.label}</Tag>
                <strong>{PLATFORM_LABEL[build.platform] || build.platform} · {VARIANT_LABEL[build.variant] || build.variant}</strong>
                <span className="devb-muted">by {PROVIDER_SHORT[build.provider] || build.provider}</span>
              </div>
              <div className="devb-item-meta devb-muted">
                {[started, build.createdBy?.email ? `started by ${build.createdBy.email}` : null, duration].filter(Boolean).join(' · ')}
              </div>
              {build.message && <p className={build.status === 'failed' ? 'devb-bad' : 'devb-message'} data-testid="devb-message">{build.message}</p>}
              {build.submit && <p className="devb-muted" data-testid="devb-submit-status">TestFlight: {build.submit.message || build.submit.status}</p>}
              {build.artifacts?.length > 0 && (
                <ul className="devb-artifacts">
                  {build.artifacts.map((artifact) => (
                    <li key={artifact.name}>
                      <span className="devb-muted">{ARTIFACT_KIND[artifact.kind] || ARTIFACT_KIND.other}{formatSize(artifact.size) ? ` · ${formatSize(artifact.size)}` : ''}</span>
                      <Button
                        data-testid={`devb-download-${artifact.name}`}
                        loading={busy === `dl:${build.id}:${artifact.name}`}
                        onClick={() => act(`dl:${build.id}:${artifact.name}`, () => downloadArtifact(projectId, build.id, artifact.name), 'The download failed.')}
                        size="small"
                        type="link"
                      >
                        Download {artifact.name}
                      </Button>
                      {artifact.kind === 'apk' && (
                        <Button data-testid={`devb-install-${artifact.name}`} onClick={() => setQr({ buildId: build.id, name: artifact.name })} size="small">
                          Install on a phone
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <div className="devb-item-actions">
                <Button data-testid="devb-view-log" onClick={() => setLog(build)} size="small">View log</Button>
                {site && build.external?.url && (
                  <Button data-testid="devb-external" href={build.external.url} rel="noopener noreferrer" size="small" target="_blank">Open in {site} ↗</Button>
                )}
                {!readOnly && isActiveBuild(build) && (
                  <Button
                    data-testid="devb-cancel"
                    loading={busy === `cancel:${build.id}`}
                    onClick={() => act(`cancel:${build.id}`, () => cancelDeviceBuild(projectId, build.id), 'The build could not be canceled.')}
                    size="small"
                  >
                    Cancel
                  </Button>
                )}
                {!readOnly && !isActiveBuild(build) && (
                  <Popconfirm
                    okText="Delete"
                    onConfirm={() => act(`delete:${build.id}`, () => deleteDeviceBuild(projectId, build.id), 'The build could not be deleted.')}
                    title="Delete this build, its log and its files?"
                  >
                    <Button danger data-testid="devb-delete" loading={busy === `delete:${build.id}`} size="small" type="text">Delete</Button>
                  </Popconfirm>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <DeviceBuildLog build={log} onClose={() => setLog(null)} open={Boolean(log)} projectId={projectId} />
      {qr && <InstallQr buildId={qr.buildId} name={qr.name} onClose={() => setQr(null)} open projectId={projectId} />}
    </>
  );
}

DeviceBuildsList.propTypes = {
  // GET /device-builds › builds of this service.
  builds: PropTypes.arrayOf(PropTypes.object),
  onChanged: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
};
